import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { adminStoryService, AdminStoryDto, QueueStats } from '../../services/adminStoryService';
import { LoadingSpinner } from '../../shared/components';
import toast from 'react-hot-toast';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const Title = styled.h1`
  font-size: 1.5rem;
  color: var(--color-gray-900);
  margin: 0;
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
`;

const StatCard = styled.div`
  background: var(--color-white);
  padding: 1.5rem;
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  text-align: center;
`;

const StatValue = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: var(--color-gray-800);
  margin-bottom: 0.5rem;
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: var(--color-gray-600);
  font-weight: 500;
`;

const AppealsGrid = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
`;

const AppealCard = styled.div<{ $priority?: 'high' | 'medium' | 'low' }>`
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
  border-left: 4px solid ${props => {
    switch (props.$priority) {
      case 'high': return '#EF4444';
      case 'medium': return '#F59E0B';
      default: return '#3B82F6';
    }
  }};
  overflow: hidden;
`;

const CardHeader = styled.div`
  padding: 1.5rem;
  border-bottom: 1px solid var(--color-gray-200);
`;

const CardTitle = styled.h3`
  margin: 0 0 0.75rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: var(--color-gray-900);
`;

const CardMeta = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  align-items: center;
  font-size: 0.875rem;
  color: var(--color-gray-600);
`;

const MetaItem = styled.span`
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const CardContent = styled.div`
  padding: 1.5rem;
`;

const Section = styled.div`
  margin-bottom: 1.5rem;
`;

const SectionTitle = styled.h4`
  margin: 0 0 0.75rem 0;
  font-size: 1rem;
  font-weight: 600;
  color: var(--color-gray-900);
`;

const ReasonBox = styled.div`
  background: var(--color-gray-50);
  border: 1px solid var(--color-gray-200);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--color-gray-700);
`;

const OriginalRejection = styled.div`
  background: var(--color-red-50);
  border: 1px solid var(--color-red-200);
  border-radius: var(--border-radius-md);
  padding: 1rem;
  font-size: 0.875rem;
  line-height: 1.5;
  color: var(--color-red-700);
`;

const ActionSection = styled.div`
  border-top: 1px solid var(--color-gray-200);
  padding: 1.5rem;
  background: var(--color-gray-50);
`;

const ActionButtons = styled.div`
  display: flex;
  gap: 0.75rem;
  margin-bottom: 1rem;
`;

const Button = styled.button<{ $variant?: 'primary' | 'danger' | 'secondary' }>`
  padding: 0.5rem 1rem;
  border-radius: var(--border-radius-md);
  font-weight: 500;
  border: 1px solid;
  cursor: pointer;
  transition: all 0.2s ease;

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${props => {
    switch (props.$variant) {
      case 'primary':
        return `
          background: var(--color-green-600);
          border-color: var(--color-green-600);
          color: white;
          &:hover:not(:disabled) {
            background: var(--color-green-700);
          }
        `;
      case 'danger':
        return `
          background: var(--color-red-600);
          border-color: var(--color-red-600);
          color: white;
          &:hover:not(:disabled) {
            background: var(--color-red-700);
          }
        `;
      default:
        return `
          background: white;
          border-color: var(--color-gray-300);
          color: var(--color-gray-700);
          &:hover:not(:disabled) {
            background: var(--color-gray-50);
          }
        `;
    }
  }}
`;

const TextArea = styled.textarea`
  width: 100%;
  min-height: 100px;
  padding: 0.75rem;
  border: 1px solid var(--color-gray-300);
  border-radius: var(--border-radius-md);
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: var(--color-primary);
    box-shadow: 0 0 0 1px var(--color-primary);
  }
`;

const EmptyState = styled.div`
  text-align: center;
  padding: 3rem;
  color: var(--color-gray-500);
  background: var(--color-white);
  border-radius: var(--border-radius-lg);
  box-shadow: var(--shadow-md);
`;

const AppealManagement: React.FC = () => {
  const [appealStories, setAppealStories] = useState<AdminStoryDto[]>([]);
  const [queueStats, setQueueStats] = useState<QueueStats | null>(null);
  const [loading, setLoading] = useState(true);
  const [processingAppeal, setProcessingAppeal] = useState<string | null>(null);
  const [responses, setResponses] = useState<Record<string, string>>({});

  useEffect(() => {
    loadAppeals();
    loadStats();
  }, []);

  const loadAppeals = async () => {
    try {
      // Get stories with APPEAL_PENDING status using existing admin stories endpoint
      const response = await adminStoryService.getPendingStories({
        status: 'APPEAL_PENDING',
        size: 50
      });
      setAppealStories(response.content || []);
    } catch (error) {
      console.error('Failed to load appeals:', error);
      toast.error('Failed to load appeals');
    }
  };

  const loadStats = async () => {
    try {
      const stats = await adminStoryService.getQueueStats();
      setQueueStats(stats);
    } catch (error) {
      console.error('Failed to load appeal stats:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAppealDecision = async (
    storyId: string,
    decision: 'APPROVED' | 'REJECTED'
  ) => {
    const response = responses[storyId];
    if (!response?.trim()) {
      toast.error('Please provide a response explaining your decision');
      return;
    }

    try {
      setProcessingAppeal(storyId);
      
      if (decision === 'APPROVED') {
        await adminStoryService.approveStory(storyId, response.trim());
        toast.success('Appeal approved - story published!');
      } else {
        await adminStoryService.rejectStory(storyId, response.trim());
        toast.success('Appeal rejected - story remains rejected');
      }
      
      // Remove the processed story from the list
      setAppealStories(prev => prev.filter(story => story.id !== storyId));
      
      // Clear the response
      setResponses(prev => {
        const updated = { ...prev };
        delete updated[storyId];
        return updated;
      });
      
      // Reload stats
      await loadStats();
    } catch (error) {
      console.error(`Failed to ${decision.toLowerCase()} appeal:`, error);
      toast.error(`Failed to ${decision.toLowerCase()} appeal`);
    } finally {
      setProcessingAppeal(null);
    }
  };

  const updateResponse = (storyId: string, response: string) => {
    setResponses(prev => ({
      ...prev,
      [storyId]: response
    }));
  };

  const calculateDaysAgo = (dateString: string) => {
    const days = Math.floor((Date.now() - new Date(dateString).getTime()) / (1000 * 60 * 60 * 24));
    return days === 0 ? 'Today' : `${days} day${days === 1 ? '' : 's'} ago`;
  };

  if (loading) {
    return (
      <Container>
        <div style={{ display: 'flex', justifyContent: 'center', padding: '3rem' }}>
          <LoadingSpinner message="Loading appeals..." />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <Header>
        <Title>Appeal Management</Title>
        <Button onClick={() => { loadAppeals(); loadStats(); }}>
          🔄 Refresh
        </Button>
      </Header>

      {/* Stats */}
      <StatsGrid>
        <StatCard>
          <StatValue>{queueStats?.appealPending || 0}</StatValue>
          <StatLabel>Pending Appeals</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{queueStats?.totalApproved || 0}</StatValue>
          <StatLabel>Total Approved</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{queueStats?.totalRejected || 0}</StatValue>
          <StatLabel>Total Rejected</StatLabel>
        </StatCard>
        <StatCard>
          <StatValue>{queueStats?.urgent || 0}</StatValue>
          <StatLabel>Urgent Items</StatLabel>
        </StatCard>
      </StatsGrid>

      {/* Appeals */}
      {appealStories.length === 0 ? (
        <EmptyState>
          <div style={{ fontSize: '3rem', marginBottom: '1rem' }}>🎉</div>
          <h3>No Pending Appeals</h3>
          <p>All appeals have been reviewed! Great work keeping up with author requests.</p>
        </EmptyState>
      ) : (
        <AppealsGrid>
          {appealStories.map(story => (
            <AppealCard key={story.id} $priority={story.urgency}>
              <CardHeader>
                <CardTitle>{story.title}</CardTitle>
                <CardMeta>
                  <MetaItem>
                    <span>👤</span>
                    Author ID: {story.authorId}
                  </MetaItem>
                  <MetaItem>
                    <span>📅</span>
                    Submitted: {calculateDaysAgo(story.submittedAt)}
                  </MetaItem>
                  <MetaItem>
                    <span>📖</span>
                    Story ID: {story.id}
                  </MetaItem>
                  <MetaItem>
                    <span>📊</span>
                    Status: {story.statusDisplayName}
                  </MetaItem>
                </CardMeta>
              </CardHeader>

              <CardContent>
                {story.reviewNotes && (
                  <Section>
                    <SectionTitle>Previous Review Notes</SectionTitle>
                    <OriginalRejection>
                      {story.reviewNotes}
                    </OriginalRejection>
                  </Section>
                )}

                <Section>
                  <SectionTitle>Story Content Preview</SectionTitle>
                  <ReasonBox>
                    {story.content ? story.content.substring(0, 300) + '...' : 'No content preview available'}
                  </ReasonBox>
                </Section>
                
                {story.summary && (
                  <Section>
                    <SectionTitle>Story Summary</SectionTitle>
                    <ReasonBox>
                      {story.summary}
                    </ReasonBox>
                  </Section>
                )}
              </CardContent>

              <ActionSection>
                <SectionTitle>Admin Decision</SectionTitle>
                <TextArea
                  value={responses[story.id] || ''}
                  onChange={(e) => updateResponse(story.id, e.target.value)}
                  placeholder="Provide a detailed response explaining your decision. This will be communicated to the author."
                />
                <ActionButtons>
                  <Button
                    $variant="primary"
                    onClick={() => handleAppealDecision(story.id, 'APPROVED')}
                    disabled={processingAppeal === story.id || !responses[story.id]?.trim()}
                  >
                    {processingAppeal === story.id ? '⏳' : '✅'} Approve Appeal
                  </Button>
                  <Button
                    $variant="danger"
                    onClick={() => handleAppealDecision(story.id, 'REJECTED')}
                    disabled={processingAppeal === story.id || !responses[story.id]?.trim()}
                  >
                    {processingAppeal === story.id ? '⏳' : '❌'} Reject Appeal
                  </Button>
                  <Button
                    onClick={() => window.open(`/admin/stories/${story.id}`, '_blank')}
                  >
                    📖 Review Full Story
                  </Button>
                </ActionButtons>
              </ActionSection>
            </AppealCard>
          ))}
        </AppealsGrid>
      )}
    </Container>
  );
};

export default AppealManagement;